import React, { FC, useLayoutEffect, useRef, useState } from 'react'
import styled, { css } from 'styled-components'
import { textSizeStyles, textWeightStyles } from '..'
import ResizeIcon from '@tuhi/components/assets/icons/buttons/resize.svg'
import { Text } from '../text/textStyles'
import { InputLabels, LabelProps } from './components/inputLabels'
import {
    InfoMessageProps,
    InputInfoMessage,
} from './components/inputInfoMessage'
import { media } from '../../theme/breakpoints'

const commonStyles = css`
    min-height: 64px;
    width: 100%;
    box-sizing: border-box;
    border: 1px solid transparent;
    padding: 16px;
    padding-bottom: 28px;

    ${textSizeStyles['xSmall']}
    ${textWeightStyles['regular']}
`

const borderStyle = css`
    outline: none;
    border-radius: 8px;
    border: 1px solid ${(p) => p.theme.colors.primary.default};
    resize: vertical;
`

const Container = styled.div`
    display: flex;
    flex-direction: column;
    gap: 8px;
`

const InputContainer = styled.div`
    display: flex;
    flex-direction: column;
    position: relative;
    gap: 8px;

    > svg {
        pointer-events: none;
        position: absolute;
        bottom: 8px;
        right: 8px;
    }

    ::-webkit-resizer {
        display: none;
    }
`

const TextContainer = styled.div`
    display: flex;
    flex-direction: column;
`

const LabelContainer = styled.div`
    display: flex;
    justify-content: space-between;
`

const Label = styled(Text)`
    text-transform: capitalize;
`

const Textarea = styled.textarea`
    ${commonStyles}
    resize: none;
    overflow-y: hidden;

    ${(p) => p.autoFocus && borderStyle}

    &:focus {
        ${borderStyle}
    }

    &:disabled {
        background: none;
    }

    ::-webkit-resizer {
        display: none;
    }

    ${media.mobile} {
        font-size: 16px;
    }
`

const HiddenSpan = styled.span`
    ${commonStyles}
    visibility: hidden;
    position: absolute;
    white-space: pre-wrap;
    top: 50%;
    left: 0;
    top: 0;
`

interface Props {
    className?: string
    labels: LabelProps
    text: string
    placeholder?: string
    infoMessage?: InfoMessageProps
    minHeight?: number
    autoFocus?: boolean
    onChange: (text: string) => void
    isEnabled?: boolean
}

export const InputFieldMultiple: FC<Props> = ({
    className,
    labels,
    text,
    placeholder,
    infoMessage,
    minHeight = 0,
    autoFocus = false,
    isEnabled = true,
    onChange,
}) => {
    const [isFocused, setIsFocused] = useState(autoFocus)

    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const hiddenSpanRef = useRef<HTMLSpanElement>(null)

    useLayoutEffect(() => {
        if (hiddenSpanRef.current && textareaRef.current) {
            // Set the hidden span's text to the textarea's value
            hiddenSpanRef.current.textContent =
                (text.length != 0 && text) || placeholder || ''
            // Get the height of the hidden span
            const newHeight =
                hiddenSpanRef.current.offsetHeight > minHeight
                    ? hiddenSpanRef.current.offsetHeight
                    : minHeight

            // Set the textarea height
            textareaRef.current.style.height = `${newHeight}px`
        }
    }, [text, placeholder]) // Update whenever text or placeholder changes

    return (
        <Container className={className}>
            <InputLabels {...labels} />

            <InputContainer>
                <Textarea
                    ref={textareaRef}
                    autoFocus={autoFocus}
                    onFocus={() => {
                        if (!autoFocus) {
                            setIsFocused(true)
                        }
                    }}
                    onBlur={() => {
                        if (!autoFocus) {
                            setIsFocused(false)
                        }
                    }}
                    placeholder={placeholder}
                    value={text}
                    onChange={(event) => onChange(event.target.value)}
                    disabled={!isEnabled}
                />
                {isFocused && <ResizeIcon />}
                <HiddenSpan ref={hiddenSpanRef} />
            </InputContainer>

            <InputInfoMessage {...infoMessage} />
        </Container>
    )
}
